import {
  Component,
  ComponentRef,
  computed,
  contentChildren,
  effect,
  EnvironmentInjector,
  inject,
  input,
  output,
  signal,
  Type,
  untracked,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  LoginFlow,
  RecoveryFlow,
  RegistrationFlow,
  SettingsFlow,
  UiNodeInputAttributesTypeEnum,
  UiText,
  VerificationFlow,
} from '@ory/client';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';

import { SharedModule } from '../../shared.module';
import { TranslatableFormError } from '../ui-kit/utils/form-error/form-error.component';
import { VKratosButtonComponent } from './components/button.component';
import { VKratosImageComponent } from './components/image.component';
import { VKratosInputComponent } from './components/input.component';
import { VKratosMessageComponent } from './components/message.component';
import { VKratosTextComponent } from './components/text.component';
import { VKratosFormFieldDirective } from './form-slot.directive';
import {
  mapUiNodeToUiWidget,
  VKratosUiAppearance,
  VKratosUiInputRenderType,
  VKratosUiWidget,
} from './types';
import { WebAuthNService } from './webauthn.service';

@Component({
  selector: 'app-kratos-form-v2',
  templateUrl: './form.component.html',
  imports: [SharedModule, ReactiveFormsModule],
  providers: [KratosAuthenticationService, WebAuthNService],
})
export class KratosFormV2Component {
  readonly flow = input<
    | RegistrationFlow
    | LoginFlow
    | RecoveryFlow
    | SettingsFlow
    | VerificationFlow
  >();

  readonly renderFallback = input(true);

  /**
   * Which kit the created widget components render with: `default` keeps the
   * legacy v2 controls (public auth pages), `vx` renders the new design
   * system's controls (settings pages).
   */
  readonly appearance = input<VKratosUiAppearance>('default');

  /**
   * Restricts rendering to these node groups (the `default` group — CSRF —
   * is always kept). A settings flow carries every method's nodes; a page
   * showing one concern (password, totp, webauthn) filters to it.
   */
  readonly groups = input<string[] | undefined>();

  /**
   * Node names to keep out of the rendered form while still tracking them —
   * for actions the page presents with its own button (e.g. a dialog footer)
   * and fires via {@link triggerAction}.
   */
  readonly excludeNames = input<string[]>([]);

  /**
   * Initial values by node name, used when the flow itself provides none —
   * e.g. prefilling the passkey display name with the current device. The
   * control stays editable; a value coming from the flow always wins.
   */
  readonly defaults = input<Record<string, string>>({});

  /**
   * While true, submits are ignored and buttons are disabled — set by the page
   * while a submit is in flight. A second social sign-in submit would
   * overwrite Kratos' OIDC state cookie and fail the first provider's callback.
   */
  readonly busy = input(false);

  public submitFlow = output<any>();

  private slots = contentChildren(VKratosFormFieldDirective, {
    descendants: true,
  });

  private fallbackHost = viewChild.required('fallbackHost', {
    read: ViewContainerRef,
  });

  private env = inject(EnvironmentInjector);
  private fb = inject(FormBuilder);
  private webauthn = inject(WebAuthNService);
  public kratosForm = new FormGroup({});

  private widgets = signal<VKratosUiWidget[]>([]);

  private widgetRefs = new Map<string, ComponentRef<any>>();
  private formMessagesRef?: ComponentRef<any>;

  private hiddenIds = computed(
    () =>
      new Set(
        this.widgets()
          .filter(w => w.attributes?.type === 'hidden')
          .map(w => w.id)
      )
  );

  // use as a function (still pure) but based on computed state
  isVisible = (id: string) => {
    const widget = this.widgets().find(w => w.id === id);
    if (!widget) {
      return false;
    }
    return !this.hiddenIds().has(id);
  };

  /** Whether the flow offers any social sign-in (OIDC) provider button. */
  readonly hasOidcProviders = computed(() =>
    this.widgets().some(
      w =>
        w.group === 'oidc' &&
        w.attributes?.name === 'provider' &&
        !this.hiddenIds().has(w.id)
    )
  );

  private formMessages = signal<UiText[]>([]);
  public hasMessages = () => {
    return this.formMessages().length > 0;
  };

  constructor() {
    effect(() => {
      const flow = this.flow();
      const slots = this.slots();

      this.formMessages.set(flow?.ui.messages ?? []);

      const groups = this.groups();
      const uiNodes = (flow?.ui.nodes ?? []).filter(
        n => !groups || n.group === 'default' || groups.includes(n.group)
      );
      const next = uiNodes
        .map(n => mapUiNodeToUiWidget(n))
        .filter((w): w is VKratosUiWidget => !!w);

      // set form messages widget

      const slot = slots.find(s => s.vKfName() === 'messages');
      if (!this.formMessagesRef) {
        this.formMessagesRef = slot?.vcr.createComponent(
          VKratosMessageComponent,
          {
            environmentInjector: this.env,
          }
        );
      }

      this.formMessagesRef?.setInput('message', this.formMessages());

      const nextIds = new Set(next.map(w => w.id));

      // ---- remove stale widgets ----
      for (const [id, ref] of this.widgetRefs) {
        if (!nextIds.has(id)) {
          const widget = this.widgets().find(w => w.id === id);

          if (widget) {
            // remove form control (image/text widgets never created one)
            const group = this.kratosForm.get(widget.group) as FormGroup;
            group?.removeControl(widget.id);

            // remove form group if empty
            if (group && Object.keys(group.controls).length === 0) {
              this.kratosForm.removeControl(widget.group);
            }
          }

          ref.destroy();
          this.widgetRefs.delete(id);
        }
      }

      this.widgets.set(next);

      // ---- add or update widgets ----
      for (const w of next) {
        const existing = this.widgetRefs.get(w.id);
        if (existing) {
          // update inputs only
          existing.setInput('widget', w);
          existing.changeDetectorRef.markForCheck();

          const control = this.kratosForm.get(w.group)?.get(w.id);
          if (w.messages && w.messages.length > 0) {
            // add an error to the corresponding form control if it exists
            control?.setErrors({
              ...control.errors,
              ...w.messages.map(
                m =>
                  new TranslatableFormError(`kratosMessages.${m.id}`, m.context)
              ),
            });

            control?.markAsTouched();
          } else {
            control?.setErrors(null);
          }

          continue;
        }

        // ensure form group / control
        let group = this.kratosForm.get(w.group) as FormGroup;
        if (!group) {
          group = this.fb.group({});
          this.kratosForm.addControl(w.group, group);
        }
        if (
          w.renderType === VKratosUiInputRenderType.Input &&
          !group.get(w.id)
        ) {
          const flowValue = w.attributes.value;
          const initialValue =
            flowValue === undefined || flowValue === null || flowValue === ''
              ? (this.defaults()[w.attributes.name] ?? flowValue)
              : flowValue;
          const ctrl = this.fb.control(
            initialValue,
            w.attributes.required ? Validators.required : []
          );

          group.addControl(w.id, ctrl);
        }

        // rendered by the consumer instead (see excludeNames)
        if (this.excludeNames().includes(w.attributes.name)) {
          continue;
        }

        // pick slot
        const slot = slots.find(s => s.vKfName() === w.id);

        if (!slot && !this.renderFallback()) {
          continue;
        }

        // an unslotted hidden input (CSRF) needs no DOM at all — its form
        // control above is what the submit payload reads
        if (!slot && w.attributes?.type === 'hidden') {
          continue;
        }

        const vcr = slot?.vcr ?? this.fallbackHost();

        // create new component
        let ref: ComponentRef<any>;
        if (
          w.renderType === VKratosUiInputRenderType.Image ||
          w.renderType === VKratosUiInputRenderType.Text
        ) {
          const displayComponent: Type<unknown> =
            w.renderType === VKratosUiInputRenderType.Image
              ? VKratosImageComponent
              : VKratosTextComponent;
          const displayRef = vcr.createComponent(displayComponent, {
            environmentInjector: this.env,
          });

          displayRef.setInput('widget', w);

          this.widgetRefs.set(w.id, displayRef);
          continue;
        } else if (w.renderType === VKratosUiInputRenderType.Input) {
          const inputRef = vcr.createComponent(VKratosInputComponent, {
            environmentInjector: this.env,
          });

          inputRef.setInput('widget', w);
          inputRef.setInput('formGroup', group);
          inputRef.setInput('appearance', this.appearance());

          if (slot?.vKfFormFieldHeader()) {
            inputRef.setInput('headerComponent', slot.vKfFormFieldHeader());
          }

          inputRef.instance.submitForm.subscribe(e => {
            // find the first submit button for this form group
            const submitButton = this.widgets().find(w => {
              return (
                w.group === e.widget.group &&
                w.type === UiNodeInputAttributesTypeEnum.Submit
              );
            });

            this.submit(e.widget.group, submitButton);
          });

          ref = inputRef;
        } else {
          const buttonRef = vcr.createComponent(VKratosButtonComponent, {
            environmentInjector: this.env,
          });

          buttonRef.setInput('formGroup', group);
          buttonRef.setInput('widget', w);
          buttonRef.setInput('appearance', this.appearance());
          buttonRef.setInput(
            'busy',
            untracked(() => this.busy())
          );
          buttonRef.instance.pressed.subscribe(e => {
            this.submit(e.widget.group, e.widget);
          });

          ref = buttonRef;
        }

        this.widgetRefs.set(w.id, ref);
      }
    });

    effect(() => {
      const busy = this.busy();

      for (const ref of this.widgetRefs.values()) {
        if (ref.instance instanceof VKratosButtonComponent) {
          ref.setInput('busy', busy);
        }
      }
    });
  }

  /**
   * Fires a button node by name — the counterpart to {@link excludeNames}
   * for actions rendered outside the form (e.g. in a dialog footer).
   */
  triggerAction(group: string, actionName: string) {
    const btn = this.widgets().find(
      w =>
        w.group === group &&
        w.renderType === VKratosUiInputRenderType.Button &&
        w.attributes.name === actionName
    );
    if (btn) {
      this.submit(group, btn);
    }
  }

  async submit(group: string, btn?: VKratosUiWidget) {
    if (this.busy()) {
      return;
    }

    // check if this is a webauthn action we need to proxy
    if (btn?.attributes.name === 'webauthn_register_trigger') {
      const result = await this.webauthn.webauthnRegistration(btn);

      const fc = this.kratosForm
        .get('webauthn')
        ?.get('webauthn-input-webauthn-register') as unknown as FormControl;
      fc.setValue(JSON.stringify(result));
    } else if (btn?.attributes.name === 'webauthn_login_trigger') {
      const result = await this.webauthn.webauthnLogin(btn);

      const fc = this.kratosForm
        .get('webauthn')
        ?.get('webauthn-input-webauthn-login') as unknown as FormControl;
      fc.setValue(JSON.stringify(result));
    }

    const defaultFormGroup = this.kratosForm.get(
      'default'
    ) as unknown as FormGroup;
    const formGroup = this.kratosForm.get(group) as FormGroup;

    let payload = {
      method: group,
    };

    for (const [name, control] of [
      ...Object.entries(defaultFormGroup.controls),
      ...Object.entries(formGroup.controls),
    ]) {
      const widget = this.widgets().find(w => w.id === name);

      payload = this.createNestedObject(
        payload,
        widget!.attributes.name,
        control.value
      );
    }

    if (btn) {
      payload = this.createNestedObject(
        payload,
        btn.attributes.name,
        btn.attributes.value
      );
    }

    this.submitFlow.emit(payload);
  }

  createNestedObject(base: any, path: string, value: any) {
    const keys = path.split('.');
    keys.reduce((prev, curr, index) => {
      return (prev[curr] =
        index === keys.length - 1 ? value : prev[curr] || {});
    }, base);
    return base;
  }
}
